import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Stack } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { getErrorMessage } from '@/errors/messages';
import { useCreateConversationFlag } from '@/hooks/useConversationFlags';
import { FEEDBACK_MAX } from './conversationFlags';

interface FlagComposerProps {
  conversationId: string;
  /** The reply to flag. Omitted, the flag is on the whole conversation. */
  messageId?: string;
  /** Closes the composer: on Cancel or Esc, and once the flag is raised. */
  onClose: () => void;
}

/**
 * The one field it takes to flag a Nim reply, or a whole conversation: what
 * Nim got wrong and what it should have done. Enter flags it, Shift+Enter
 * starts a new line, and Esc closes the composer.
 *
 * Mounted once per opening, and so is its idempotency key: Enter again after
 * a failed or dropped request resends the same key, and the server returns
 * the flag it already raised rather than raising a second.
 */
export function FlagComposer({ conversationId, messageId, onClose }: FlagComposerProps) {
  const createFlag = useCreateConversationFlag();
  const [feedback, setFeedback] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const trimmed = feedback.trim();

  const submit = () => {
    if (!trimmed || createFlag.isPending) return;
    createFlag.mutate({ conversationId, messageId, feedback: trimmed, idempotencyKey }, { onSuccess: onClose });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Stack spacing={1} data-testid="flag-composer">
      <Textarea
        aria-label="Feedback"
        value={feedback}
        onChange={setFeedback}
        placeholder={
          messageId
            ? 'What did Nim get wrong here, and what should it have done?'
            : 'What did Nim get wrong in this conversation, and what should it have done?'
        }
        autoFocus
        rows={2}
        maxRows={8}
        maxLength={FEEDBACK_MAX}
        // Read-only rather than disabled while it sends, so the field keeps focus
        // and a second Enter after a failure needs no click.
        readOnly={createFlag.isPending}
        onKeyDown={handleKeyDown}
        error={createFlag.isError ? `Could not flag this: ${getErrorMessage(createFlag.error)}` : undefined}
        helper="Enter to flag, Shift+Enter for a new line, Esc to cancel"
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="link" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          startIcon="flag"
          onClick={submit}
          disabled={!trimmed}
          loading={createFlag.isPending}
        >
          Flag
        </Button>
      </Stack>
    </Stack>
  );
}
